import { z } from "zod";
import { competenciaSchema, dataParaTexto, dataSchema, textoParaData } from "./competencia";
import { FORMAS_DE_PAGAMENTO } from "./lancamento";

export const COBRANCAS_DO_EXTRATO = ["CLINICA", "DENTISTA"] as const;
export type CobrancaDoExtrato = (typeof COBRANCAS_DO_EXTRATO)[number];
export const COBRANCA_LABELS: Record<CobrancaDoExtrato, string> = { CLINICA: "Por clínica", DENTISTA: "Por dentista" };

export const FREQUENCIAS_DO_EXTRATO = ["MENSAL", "SEMANAL", "POR_PEDIDO"] as const;
export type FrequenciaDoExtrato = (typeof FREQUENCIAS_DO_EXTRATO)[number];
export const FREQUENCIA_LABELS: Record<FrequenciaDoExtrato, string> = { MENSAL: "Mensal", SEMANAL: "Semanal", POR_PEDIDO: "Por pedido" };

export const TIPOS_DE_ITEM = ["SERVICO", "RETRABALHO", "ACRESCIMO"] as const;
export type TipoDeItem = (typeof TIPOS_DE_ITEM)[number];

export const DIAS_DA_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"] as const;

export type RegraDeExtrato = {
  cobranca: CobrancaDoExtrato;
  frequencia: FrequenciaDoExtrato;
  diaDeFechamento: number;
  diaDaSemana: number;
  prazoDeVencimentoDias: number;
};
export type RegraDoCliente = { [K in keyof RegraDeExtrato]?: RegraDeExtrato[K] | null };

/** O que o cliente tem de próprio vence o padrão; o resto vem do padrão. */
export function regraEfetiva(padrao: RegraDeExtrato, doCliente?: RegraDoCliente | null): RegraDeExtrato {
  return {
    cobranca: doCliente?.cobranca ?? padrao.cobranca,
    frequencia: doCliente?.frequencia ?? padrao.frequencia,
    diaDeFechamento: doCliente?.diaDeFechamento ?? padrao.diaDeFechamento,
    diaDaSemana: doCliente?.diaDaSemana ?? padrao.diaDaSemana,
    prazoDeVencimentoDias: doCliente?.prazoDeVencimentoDias ?? padrao.prazoDeVencimentoDias,
  };
}

const diasNoMes = (ano: number, mes0: number) => new Date(Date.UTC(ano, mes0 + 1, 0)).getUTCDate();
/** Dia de fechamento no mês: 31 num mês de 30 dias é o dia 30 (e 28/29 em fevereiro). */
const fechamentoNoMes = (ano: number, mes0: number, dia: number) => new Date(Date.UTC(ano, mes0, Math.min(dia, diasNoMes(ano, mes0))));
const somarDias = (texto: string, n: number) => dataParaTexto(new Date(textoParaData(texto).getTime() + n * 86_400_000));

/** O fechamento mais recente até hoje (inclusive), "AAAA-MM-DD". Por pedido não tem fechamento. */
export function ultimoFechamento(regra: Pick<RegraDeExtrato, "frequencia" | "diaDeFechamento" | "diaDaSemana">, hoje: string): string | null {
  const d = textoParaData(hoje);
  if (regra.frequencia === "SEMANAL") return somarDias(hoje, -((d.getUTCDay() - regra.diaDaSemana + 7) % 7));
  if (regra.frequencia !== "MENSAL") return null;
  const ano = d.getUTCFullYear();
  const mes0 = d.getUTCMonth();
  const desteMes = fechamentoNoMes(ano, mes0, regra.diaDeFechamento);
  return dataParaTexto(d >= desteMes ? desteMes : fechamentoNoMes(ano, mes0 - 1, regra.diaDeFechamento));
}

/** O próximo fechamento depois de hoje. */
export function proximoFechamento(regra: Pick<RegraDeExtrato, "frequencia" | "diaDeFechamento" | "diaDaSemana">, hoje: string): string | null {
  const d = textoParaData(hoje);
  if (regra.frequencia === "SEMANAL") return somarDias(hoje, (regra.diaDaSemana - d.getUTCDay() + 7) % 7 || 7);
  if (regra.frequencia !== "MENSAL") return null;
  const desteMes = fechamentoNoMes(d.getUTCFullYear(), d.getUTCMonth(), regra.diaDeFechamento);
  return dataParaTexto(d < desteMes ? desteMes : fechamentoNoMes(d.getUTCFullYear(), d.getUTCMonth() + 1, regra.diaDeFechamento));
}

export const vencimentoDoExtrato = (emissao: string, prazoDias: number) => somarDias(emissao, prazoDias);

/** Itens do mesmo pedido juntos (o Jun agrupa por pedido mostrando os serviços), pedidos pela data. */
export function agruparPorPedido<T extends { pedidoId: string; data: string }>(itens: T[]): { pedidoId: string; itens: T[] }[] {
  const grupos = new Map<string, T[]>();
  for (const i of [...itens].sort((a, b) => a.data.localeCompare(b.data))) {
    grupos.set(i.pedidoId, [...(grupos.get(i.pedidoId) ?? []), i]);
  }
  return [...grupos].map(([pedidoId, lista]) => ({ pedidoId, itens: lista }));
}

export type SituacaoDoExtrato = "CANCELADO" | "QUITADO" | "PERDIDO" | "CREDITO" | "VENCIDO" | "PARCIAL" | "EM_ABERTO";
export const SITUACAO_DO_EXTRATO_LABELS: Record<SituacaoDoExtrato, string> = {
  CANCELADO: "Cancelado",
  QUITADO: "Quitado",
  PERDIDO: "Dado como perdido",
  CREDITO: "Pagou a mais",
  VENCIDO: "Vencido",
  PARCIAL: "Parcial",
  EM_ABERTO: "Em aberto",
};

export function situacaoDoExtrato(e: { status: "EMITIDO" | "CANCELADO"; totalCents: number; recebidoCents: number; perdidoCents?: number; vencimento: string }, hoje: string): SituacaoDoExtrato {
  const perdido = e.perdidoCents ?? 0;
  if (e.status === "CANCELADO") return "CANCELADO";
  if (e.recebidoCents > e.totalCents) return "CREDITO";
  if (e.recebidoCents + perdido >= e.totalCents) return perdido > 0 ? "PERDIDO" : "QUITADO";
  if (e.vencimento < hoje) return "VENCIDO";
  return e.recebidoCents > 0 ? "PARCIAL" : "EM_ABERTO";
}

// ─── Entradas da API ─────────────────────────────────────────────

const textoOpcional = (max: number) => z.string().trim().max(max).nullable().optional().transform((v) => v || null);

export const configuracaoDeExtratoSchema = z.object({
  cobranca: z.enum(COBRANCAS_DO_EXTRATO),
  frequencia: z.enum(FREQUENCIAS_DO_EXTRATO),
  diaDeFechamento: z.number().int().min(1).max(31),
  diaDaSemana: z.number().int().min(0).max(6),
  prazoDeVencimentoDias: z.number().int().min(0).max(120),
  inicioDosExtratos: competenciaSchema,
});
export type ConfiguracaoDeExtratoInput = z.infer<typeof configuracaoDeExtratoSchema>;

/** O que vai no documento — do perfil do laboratório, vale pro extrato simples (grátis) e o emitido. */
export const documentoDoExtratoSchema = z.object({
  mostrarDentista: z.boolean(),
  mostrarDentes: z.boolean(),
  mostrarRefacaoGratuita: z.boolean(),
  whatsapp: textoOpcional(30),
  email: z.union([z.literal(""), z.string().trim().email("E-mail inválido").max(160)]).nullable().optional().transform((v) => v || null),
  instagram: textoOpcional(60),
  formasDePagamento: textoOpcional(600),
});
export type DocumentoDoExtratoInput = z.infer<typeof documentoDoExtratoSchema>;

/** Extrato simples (Cadastros, grátis): o que foi entregue no período, lido na hora. */
export const extratoSimplesSchema = z
  .object({
    partnerId: z.string().min(1),
    desde: dataSchema,
    ate: dataSchema,
    dentistaId: z.string().min(1).nullable().optional(),
  })
  .refine((v) => v.desde <= v.ate, { message: "O período termina antes de começar", path: ["ate"] });

export const regraDoClienteSchema = z.object({
  clientOrganizationId: z.string().min(1),
  cobranca: z.enum(COBRANCAS_DO_EXTRATO).nullable(),
  frequencia: z.enum(FREQUENCIAS_DO_EXTRATO).nullable(),
  diaDeFechamento: z.number().int().min(1).max(31).nullable(),
  diaDaSemana: z.number().int().min(0).max(6).nullable(),
  prazoDeVencimentoDias: z.number().int().min(0).max(120).nullable(),
});
export type RegraDoClienteInput = z.infer<typeof regraDoClienteSchema>;

export const destinatarioDoExtratoSchema = z.object({
  clientOrganizationId: z.string().min(1),
  /** Preenchido quando a regra do cliente cobra por dentista. */
  dentistaId: z.string().min(1).nullable(),
});

/** Itens escolhidos, como "S:<serviço>", "R:<retrabalho>", "A:<acréscimo>". */
export const emitirExtratoSchema = destinatarioDoExtratoSchema.extend({
  itens: z.array(z.string().regex(/^[SRA]:.+/)).min(1, "Escolha ao menos um item").max(2000),
  rotulo: competenciaSchema,
  vencimento: dataSchema,
});
export type EmitirExtratoInput = z.infer<typeof emitirExtratoSchema>;

export const cancelarExtratoSchema = z.object({ id: z.string().min(1), motivo: textoOpcional(300) });

export const recebimentoDoExtratoSchema = z.object({
  extratoId: z.string().min(1),
  valorCents: z.number().int().positive("Informe o valor"),
  pagoEm: dataSchema,
  formaDePagamento: z.enum(FORMAS_DE_PAGAMENTO).nullable().optional(),
  observacao: z.string().trim().max(500).nullable().optional(),
});
